// Vehicle → Legal & Safety.
//
// Four independent answers, never one blended figure: operational status,
// legal readiness, insurance, safety (from Enhanced Vehicle Safety, read
// only), plus the combined release constraint that shows which layer blocks.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { errorMessage, lrGet, lrPost, lrPut, statusLabel } from "../../lib/clientLegal";
import { Button, Empty, Field, Loading, Modal, Note, Section, inputClass } from "../platform-legal/ui";
import RequirementDetail, { StatusPill } from "./RequirementDetail";

const SECTIONS = [
  { title: "Registration", keys: ["lto_registration", "vehicle_identity_documents", "pmvic_inspection", "emissions_test"] },
  { title: "Ownership / use authority", keys: ["vehicle_ownership_control", "vehicle_finance_authority",
    "vehicle_ownership_transfer", "motorcycle_transfer_compliance"] },
  { title: "Rental / transport authority", keys: ["unit_transport_authority"] },
  { title: "Insurance", keys: ["ctpl_insurance", "rental_use_insurance"] },
  { title: "Safety equipment", keys: ["safety_equipment"] },
];

export default function VehicleLegalPanel({ vehicleId, options }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [editProfile, setEditProfile] = useState(false);

  const load = useCallback(() => lrGet(`/vehicles/${vehicleId}`)
    .then((d) => { setData(d); setError(null); })
    .catch((err) => setError(err?.response?.status === 403
      ? "You don't have access to vehicle legal readiness." : errorMessage(err))), [vehicleId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Section><Empty testId="vl-no-access">{error}</Empty></Section>;
  if (!data) return <Loading />;
  const byKey = Object.fromEntries(data.requirements.map((r) => [r.key, r]));
  const rr = data.rental_readiness;

  return (
    <div className="space-y-3" data-testid="vehicle-legal-panel">
      <Section title="Legal & Safety" description="Each answer is separate. Valid papers do not make a vehicle safe,
               and a safe vehicle is not automatically legally ready.">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="vl-summary">
          <Card label="Operational status" value={data.operational_status || "—"} plain />
          <Card label="Legal readiness" status={data.legal_status} testId="vl-legal" />
          <Card label="Compulsory insurance" status={data.insurance_status} testId="vl-insurance" />
          <Card label="Safety (Enhanced Vehicle Safety)" status={data.safety.status} testId="vl-safety" />
          <Card label="Transport authority" status={data.transport_status} testId="vl-transport" />
        </div>
        <div className="mt-3 rounded-xl border border-slate-200 p-3" data-testid="vl-release">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">Ready for rental:</span>
            <StatusPill status={rr.status} testId="vl-release-status" />
          </div>
          {rr.blockers.length > 0 && (
            <ul className="mt-1 text-xs text-rose-800 list-disc pl-5">
              {rr.blockers.map((b) => <li key={b.layer + b.code}>{b.layer.replace(/_/g, " ")}: {statusLabel(b.code)}
                {b.detail?.length ? ` (${b.detail.join(", ")})` : ""}</li>)}
            </ul>
          )}
          {rr.note && <p className="mt-1 text-xs text-slate-600">{rr.note}</p>}
          {data.safety.note && <p className="mt-1 text-xs text-slate-500">{data.safety.note}</p>}
        </div>
        {data.reasons.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="vl-reasons">
            {data.reasons.map((r, i) => (
              <li key={`${r.code}-${i}`} className="text-xs text-slate-700">
                <span className="font-semibold">{r.title}</span>{r.confirmed ? " (confirmed)" : ""}: {r.explanation}
              </li>
            ))}
          </ul>
        )}
        {data.registration.schedule && (
          <p className="mt-2 text-xs text-slate-500">
            Plate-based renewal schedule (advisory, {data.registration.schedule.rule_version}): month{" "}
            {data.registration.schedule.scheduled_month}
            {data.registration.schedule.scheduled_week ? `, week ${data.registration.schedule.scheduled_week}` : ""}.
            The OR validity date always controls.
          </p>
        )}
        {data.can_manage && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setEditProfile(true)} data-testid="vl-edit-profile">
              Ownership & service modes
            </Button>
          </div>
        )}
      </Section>

      {SECTIONS.map((section) => {
        const rows = section.keys.map((k) => byKey[k]).filter(Boolean);
        if (!rows.length) return null;
        return (
          <Section key={section.title} title={section.title}>
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.key}>
                  <button type="button" onClick={() => setOpen(r.key)}
                          className="w-full text-left rounded-xl border border-slate-200 p-3 hover:bg-slate-50"
                          data-testid={`vl-req-${r.key}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900">{r.title}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-600">
                      {r.applicable ? (r.expiry_date ? `Expiry ${r.expiry_date}` : r.short_description)
                        : r.applicability_reason}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        );
      })}
      <Note tone="slate">{data.disclaimer}</Note>

      {open && (
        <RequirementDetail requirementKey={open} scopeId={vehicleId} options={options}
                           onClose={() => setOpen(null)} onChanged={load} />
      )}
      {editProfile && (
        <VehicleProfileModal vehicleId={vehicleId} data={data} options={options}
                             onClose={() => setEditProfile(false)} onSaved={() => { setEditProfile(false); load(); }} />
      )}
    </div>
  );
}

function Card({ label, status, value, plain, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-1">{plain ? <span className="text-sm text-slate-800">{value}</span> : <StatusPill status={status} />}</div>
    </div>
  );
}

function VehicleProfileModal({ vehicleId, data, options, onClose, onSaved }) {
  const [form, setForm] = useState({ ownership_status: data.ownership_status || "",
    service_modes: data.service_modes || [], transfer_pending: false, registered_owner_is_business: true });
  const [hold, setHold] = useState({ hold: "IMPOUNDED", reason: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.ownership_status) delete payload.ownership_status;
      await lrPut(`/vehicles/${vehicleId}/legal-profile`, payload);
      toast.success("Saved.");
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const setLegalHold = async (active) => {
    setBusy(true);
    try {
      await lrPost(`/vehicles/${vehicleId}/legal-holds`, { ...hold, active });
      toast.success(active ? "Legal hold recorded." : "Legal hold cleared.");
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const toggleMode = (m) => setForm({ ...form, service_modes: form.service_modes.includes(m)
    ? form.service_modes.filter((x) => x !== m) : [...form.service_modes, m] });
  return (
    <Modal title="Ownership & service modes" onClose={onClose} testId="vl-profile-modal">
      <Field label="Ownership / control">
        <select className={inputClass} value={form.ownership_status}
                onChange={(e) => setForm({ ...form, ownership_status: e.target.value })}>
          <option value="">Not recorded</option>
          {(options?.ownership_statuses || []).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ").toLowerCase()}</option>)}
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.registered_owner_is_business}
               onChange={(e) => setForm({ ...form, registered_owner_is_business: e.target.checked })} />
        The registered owner on the CR is this business
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.transfer_pending}
               onChange={(e) => setForm({ ...form, transfer_pending: e.target.checked })} />
        An ownership transfer is still pending
      </label>
      <fieldset>
        <legend className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Services this unit is used for</legend>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {(options?.rental_modes || []).map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.service_modes.includes(m)} onChange={() => toggleMode(m)} />
              {m.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex justify-end"><Button busy={busy} onClick={save} data-testid="vl-profile-save">Save</Button></div>
      <hr />
      <Field label="Record a legal hold (impounded, stolen, legal prohibition)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select className={inputClass} value={hold.hold} onChange={(e) => setHold({ ...hold, hold: e.target.value })}>
            {(options?.legal_holds || []).map((h) => <option key={h} value={h}>{h.replace(/_/g, " ").toLowerCase()}</option>)}
          </select>
          <input className={inputClass} placeholder="Reason (required)" value={hold.reason}
                 onChange={(e) => setHold({ ...hold, reason: e.target.value })} />
        </div>
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" busy={busy} onClick={() => setLegalHold(false)}>Clear hold</Button>
        <Button variant="danger" busy={busy} onClick={() => setLegalHold(true)}>Set hold</Button>
      </div>
    </Modal>
  );
}
